import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Sparkles,
  Wrench,
  MapPin,
  FileText,
  CalendarClock,
  Languages,
  Brush,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import { services } from "@/lib/services-data";
import { locations } from "@/lib/locations-data";
import { business } from "@/lib/business-data";
import { imgSize } from "@/lib/image-dimensions";

const reasons = [
  {
    icon: Wrench,
    title: "One Crew, Every Trade",
    desc: "Drywall, paint, plumbing, electrical, tile, and carpentry handled in-house. You're not coordinating four contractors and four calendars to finish one room.",
  },
  {
    icon: MapPin,
    title: "Local, Not Dispatched",
    desc: `We work out of ${business.address.city}. That means shorter drives, faster scheduling, and a crew that already knows the housing stock in your neighborhood.`,
  },
  {
    icon: FileText,
    title: "Free Estimates, in Writing",
    desc: "Every job starts with a walkthrough and a written scope before anything is committed. No hourly ambiguity, no discovering the price after the work is done.",
  },
  {
    icon: CalendarClock,
    title: "Six Days a Week, 7 to 7",
    desc: "Monday through Saturday, 7:00 AM to 7:00 PM. Work happens when it suits your schedule, not only during a narrow midweek window.",
  },
  {
    icon: Languages,
    title: "English and Spanish",
    desc: "Every conversation — the estimate, the scope, the questions midway through — happens in whichever language you're most comfortable with.",
  },
  {
    icon: Brush,
    title: "We Leave It Cleaner Than We Found It",
    desc: "Floors and fixtures protected before work starts, debris removed at the end, and a final walkthrough together before we call anything finished.",
  },
];

const WhyChooseUs = () => {
  const { openQuoteForm } = useQuoteForm();

  const stats = [
    { value: `${services.length}`, label: "Services Offered" },
    { value: `${locations.length}`, label: "Cities Served" },
    { value: `${business.serviceRadiusMiles}mi`, label: "Service Radius" },
    { value: "6", label: "Days a Week" },
  ];

  return (
    <section className="relative section-padding section-soft-alt overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute -top-32 -right-32 w-[34rem] h-[34rem] rounded-full blur-3xl opacity-[0.16] pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--primary)), transparent 70%)" }}
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-40 -left-32 w-[30rem] h-[30rem] rounded-full blur-3xl opacity-[0.12] pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--secondary)), transparent 70%)" }}
      />

      <div className="container-max max-w-[1200px] relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Editorial intro */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <motion.span
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="eyebrow-pill mb-6"
              >
                <Sparkles className="h-3 w-3" /> Why Choose Us
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="font-heading font-bold text-foreground tracking-tight leading-[1.03]"
                style={{ fontSize: "clamp(2.25rem, 4.6vw, 4rem)" }}
              >
                Six reasons{" "}
                <span className="relative inline-block">
                  <span className="text-gradient-primary">homeowners</span>
                  <span
                    aria-hidden="true"
                    className="absolute left-0 right-0 -bottom-2 h-[6px] rounded-full opacity-80"
                    style={{
                      background: "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--secondary)))",
                    }}
                  />
                </span>
                <br />
                call us back.
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="mt-7 text-foreground/75 text-lg leading-relaxed max-w-md"
              >
                Nothing here is a slogan. Each one is something you can hold us to on the first
                job — and the reason most of our work comes from repeat customers and referrals.
              </motion.p>

              {/* Factual stat strip */}
              <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-6 max-w-md">
                {stats.map((s, i) => (
                  <motion.div
                    key={s.label}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 + i * 0.05 }}
                    className="border-l-[3px] border-primary pl-4"
                  >
                    <div className="font-heading font-bold text-4xl text-foreground tracking-tight leading-none">
                      {s.value}
                    </div>
                    <div className="mt-2 text-xs uppercase tracking-[0.14em] text-muted-foreground font-bold">
                      {s.label}
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <Button size="lg" onClick={openQuoteForm} className="gap-2 btn-glow rounded-full px-6">
                  Get Free Estimate <ArrowUpRight className="h-4 w-4" />
                </Button>
                <Button asChild size="lg" variant="outline" className="gap-2 rounded-full px-6">
                  <Link to="/about">About Our Team</Link>
                </Button>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.25, duration: 0.6 }}
                className="mt-10 rounded-2xl overflow-hidden border border-border/70 shadow-premium"
              >
                <img
                  src="/assets/images/why-choose-us.webp"
                  alt={`${business.name} carpenter measuring and cutting trim on a job site`}
                  className="w-full h-auto object-cover"
                  loading="lazy"
                  decoding="async"
                  {...imgSize("/assets/images/why-choose-us.webp")}
                />
              </motion.div>
            </div>
          </div>

          {/* Numbered reasons */}
          <div className="lg:col-span-7">
            <ul className="divide-y divide-border border-y border-border">
              {reasons.map((r, i) => {
                const Icon = r.icon;
                return (
                  <motion.li
                    key={r.title}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.25 }}
                    transition={{ delay: i * 0.05, duration: 0.5, ease: [0.33, 1, 0.68, 1] }}
                    className="group relative"
                  >
                    <span
                      aria-hidden="true"
                      className="absolute left-0 top-0 bottom-0 w-[3px] origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-[cubic-bezier(0.33,1,0.68,1)]"
                      style={{ background: "linear-gradient(180deg, hsl(var(--primary)), hsl(var(--secondary)))" }}
                    />
                    <div className="flex items-start gap-5 sm:gap-7 py-7 sm:py-8 pl-5 sm:pl-7 pr-2 transition-colors duration-500 group-hover:bg-primary/[0.04]">
                      <div className="shrink-0 flex flex-col items-start gap-3">
                        <span
                          className="font-heading font-bold text-[2.25rem] sm:text-[2.75rem] leading-none text-primary/35 group-hover:text-primary transition-colors duration-500"
                          aria-hidden="true"
                        >
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-primary/12 text-primary ring-1 ring-primary/25 group-hover:bg-primary group-hover:text-primary-foreground group-hover:ring-primary transition-all duration-500">
                          <Icon className="h-5 w-5" strokeWidth={1.9} />
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <h3 className="font-heading font-bold text-xl sm:text-2xl text-foreground tracking-tight leading-snug">
                            {r.title}
                          </h3>
                          <ArrowUpRight
                            aria-hidden="true"
                            className="h-5 w-5 text-primary/50 shrink-0 mt-1 transition-all duration-500 group-hover:text-primary group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                          />
                        </div>
                        <p className="mt-3 text-foreground/75 text-[15px] sm:text-base leading-relaxed max-w-[62ch]">
                          {r.desc}
                        </p>
                      </div>
                    </div>
                  </motion.li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
